import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'App';
import {
  CreatePermissionGate,
  DeletePermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { adminService } from 'views/services/adminService';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import { EConfigStatus, IInvoiceFeeRule } from 'views/types/invoicePayments';
import {
  cellClass,
  dangerSmallBtn,
  emptyToNull,
  headerClass,
  inputCls,
  labelCls,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './shared';

interface IDraft {
  sender_id: string;
  currency: string;
  rail: string;
  amount_min: string;
  amount_max: string;
  fee_percentage: string;
  status: EConfigStatus;
}

const EMPTY: IDraft = {
  sender_id: '',
  currency: 'USD',
  rail: 'SWIFT',
  amount_min: '0',
  amount_max: '',
  fee_percentage: '',
  status: EConfigStatus.ACTIVE,
};

export const FeeRulesPanel = () => {
  const [draft, setDraft] = useState<IDraft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: rules, mutate } = useSWR(
    '/api/admin/invoice-payment-config/fee-rules',
    () => invoicePaymentsService.getFeeRules()
  );
  const { data: meta } = useSWR('/api/admin/invoice-payment-config/meta', () =>
    invoicePaymentsService.getMeta()
  );
  const { data: senders } = useSWR(
    '/api/admin/invoice-payment-config/senders',
    () => invoicePaymentsService.getSenders()
  );

  const senderName = (id?: string | null) =>
    id ? (senders?.find(s => s.id === id)?.name ?? id.slice(0, 8)) : 'Global';

  const startEdit = (rule: IInvoiceFeeRule) => {
    setEditingId(rule.id);
    setDraft({
      sender_id: rule.sender_id ?? '',
      currency: rule.currency,
      rail: rule.rail,
      amount_min: rule.amount_min,
      amount_max: rule.amount_max ?? '',
      fee_percentage: rule.fee_percentage,
      status: rule.status,
    });
  };

  const submit = async () => {
    if (!draft.fee_percentage.trim()) {
      toast({ title: 'Fee percentage is required', status: 'error' });
      return;
    }
    const payload = {
      sender_id: emptyToNull(draft.sender_id),
      currency: draft.currency.trim().toUpperCase(),
      rail: draft.rail,
      amount_min: draft.amount_min.trim() || '0',
      amount_max: emptyToNull(draft.amount_max),
      fee_percentage: draft.fee_percentage.trim(),
      status: draft.status,
    };
    try {
      setBusy(true);
      if (editingId) {
        await invoicePaymentsService.updateFeeRule(editingId, payload);
        toast({ title: 'Fee rule updated', status: 'success' });
      } else {
        await invoicePaymentsService.createFeeRule(payload);
        toast({ title: 'Fee rule created', status: 'success' });
      }
      setDraft(EMPTY);
      setEditingId(null);
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this fee rule?')) return;
    try {
      setBusy(true);
      await invoicePaymentsService.deleteFeeRule(id);
      toast({ title: 'Fee rule deleted', status: 'success' });
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <CreatePermissionGate resource={PERMISSION_RESOURCES.INVOICE_CONFIG}>
        <div className="mb-4 grid grid-cols-2 gap-3 rounded border border-gray-200 p-3 dark:border-white/20 md:grid-cols-4 xl:grid-cols-7">
          <div>
            <label className={labelCls}>Sender</label>
            <select
              className={inputCls}
              value={draft.sender_id}
              onChange={e => setDraft({ ...draft, sender_id: e.target.value })}
            >
              <option value="">Global (all senders)</option>
              {senders?.map(sender => (
                <option key={sender.id} value={sender.id}>
                  {sender.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Currency</label>
            <input
              className={inputCls}
              value={draft.currency}
              onChange={e => setDraft({ ...draft, currency: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Payment method</label>
            <select
              className={inputCls}
              value={draft.rail}
              onChange={e => setDraft({ ...draft, rail: e.target.value })}
            >
              {meta?.rails.map(rail => (
                <option key={rail} value={rail}>
                  {rail}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Amount from</label>
            <input
              className={inputCls}
              value={draft.amount_min}
              onChange={e => setDraft({ ...draft, amount_min: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Amount to (empty = ∞)</label>
            <input
              className={inputCls}
              value={draft.amount_max}
              onChange={e => setDraft({ ...draft, amount_max: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Fee %</label>
            <input
              className={inputCls}
              value={draft.fee_percentage}
              onChange={e =>
                setDraft({ ...draft, fee_percentage: e.target.value })
              }
            />
          </div>
          <div className="flex items-end gap-2">
            <button disabled={busy} onClick={submit} className={primaryBtn}>
              {editingId ? 'Save' : 'Add rule'}
            </button>
            {editingId && (
              <button
                className={secondaryBtn}
                onClick={() => {
                  setEditingId(null);
                  setDraft(EMPTY);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </CreatePermissionGate>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={headerClass}>Sender</th>
              <th className={headerClass}>Currency</th>
              <th className={headerClass}>Method</th>
              <th className={headerClass}>Amount range</th>
              <th className={headerClass}>Fee %</th>
              <th className={headerClass}>Status</th>
              <th className={headerClass}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules?.map(rule => (
              <tr key={rule.id}>
                <td className={cellClass}>{senderName(rule.sender_id)}</td>
                <td className={cellClass}>{rule.currency}</td>
                <td className={cellClass}>{rule.rail}</td>
                <td className={cellClass}>
                  {rule.amount_min} – {rule.amount_max ?? '∞'}
                </td>
                <td className={cellClass}>{rule.fee_percentage}%</td>
                <td className={cellClass}>
                  <span
                    className={
                      rule.status === EConfigStatus.ACTIVE
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }
                  >
                    {rule.status}
                  </span>
                </td>
                <td className={`${cellClass} flex gap-2`}>
                  <UpdatePermissionGate
                    resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                  >
                    <button
                      className={smallBtn}
                      onClick={() => startEdit(rule)}
                    >
                      Edit
                    </button>
                  </UpdatePermissionGate>
                  <DeletePermissionGate
                    resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                  >
                    <button
                      className={dangerSmallBtn}
                      disabled={busy}
                      onClick={() => remove(rule.id)}
                    >
                      Delete
                    </button>
                  </DeletePermissionGate>
                </td>
              </tr>
            ))}
            {rules && rules.length === 0 && (
              <tr>
                <td
                  className="py-4 text-center text-sm text-gray-500"
                  colSpan={7}
                >
                  No fee rules.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
